import React, { useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn } from "@/lib/styles.utils";
import Required from "../required/Required";

export type SelectOption = { label: string; value: string; disabled?: boolean };

interface CustomSelectProps {
  size: "large" | "medium" | "small";
  label?: string;
  placeholder?: string;
  required?: boolean;
  titleInPanel?: string;
  options: SelectOption[];
  value?: string | null;
  onChange?: (value: string) => void; // limpiar => onChange("")
  disabled?: boolean;
  name?: string;
  error?: boolean;
  id: string;
  children?: React.ReactNode;
}

const InputSearchSelect = ({
  size,
  label,
  placeholder,
  required,
  titleInPanel,
  options,
  value = null,
  onChange,
  disabled,
  name,
  error,
  id,
  children,
}: CustomSelectProps) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const scrollParentRef = useRef<HTMLDivElement | null>(null);

  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [internalValue, setInternalValue] = useState<string | null>(value);
  const [query, setQuery] = useState("");

  // Sync con value externo
  useEffect(() => setInternalValue(value ?? null), [value]);

  useEffect(() => {
    const newLabel =
      value != null
        ? (options.find((o) => o.value === value)?.label ?? "")
        : "";
    setQuery(newLabel);
  }, [value, options]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const rowHeight = useMemo(() => {
    if (size === "small") return 40;
    if (size === "medium") return 48;
    return 56;
  }, [size]);

  const maxPanelHeight = useMemo(() => {
    if (size === "small") return 240;
    if (size === "medium") return 256;
    return 272;
  }, [size]);

  const heights = useMemo(() => {
    if (size === "small")
      return {
        font: "text-sm",
        optionsGroup: "max-h-60",
      };
    if (size === "medium")
      return {
        font: "text-base",
        optionsGroup: "max-h-64",
      };
    return {
      font: "text-lg",
      optionsGroup: "max-h-[272px]",
    };
  }, [size]);

  const panelHeight = useMemo(() => {
    return Math.min(maxPanelHeight, filteredOptions.length * rowHeight);
  }, [filteredOptions.length, maxPanelHeight, rowHeight]);

  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => scrollParentRef.current,
    estimateSize: () => rowHeight,
    overscan: 8,
  });

  // Scroll al highlight
  useEffect(() => {
    if (!open) return;
    if (highlight < 0) return;
    virtualizer.scrollToIndex(highlight, { align: "auto" });
  }, [highlight, open, virtualizer]);

  // Cerrar click fuera
  useEffect(() => {
    if (!open) return;
    const handler = (ev: MouseEvent) => {
      const t = ev.target as Node;
      if (rootRef.current?.contains(t)) return;
      setOpen(false);
      setHighlight(-1);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const commitChange = (newVal: string | null) => {
    setInternalValue(newVal);

    if (newVal == null) {
      setQuery("");
      onChange?.("");
      setOpen(false);
      setHighlight(-1);
      return;
    }

    const opt = options.find((o) => o.value === newVal);
    setQuery(opt?.label ?? "");
    onChange?.(newVal);
    setOpen(false);
    setHighlight(-1);
  };

  const validateOnBlur = () => {
    const q = query.trim();
    if (!q) return commitChange(null);

    const exact = options.find(
      (o) => o.label.trim().toLowerCase() === q.toLowerCase(),
    );

    if (!exact || exact.disabled) return commitChange(null);
    return commitChange(exact.value);
  };

  const moveHighlight = (dir: 1 | -1) => {
    if (filteredOptions.length === 0) return;

    let next = highlight;
    for (let i = 0; i < filteredOptions.length; i++) {
      next = (next + dir + filteredOptions.length) % filteredOptions.length;
      if (!filteredOptions[next]?.disabled) break;
    }
    setHighlight(next);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!open) setOpen(true);
        moveHighlight(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!open) setOpen(true);
        moveHighlight(-1);
        break;
      case "Enter":
        if (open && highlight >= 0) {
          e.preventDefault();
          const opt = filteredOptions[highlight];
          if (opt && !opt.disabled) commitChange(opt.value);
        }
        break;
      case "Escape":
        if (open) {
          e.preventDefault();
          setOpen(false);
          setHighlight(-1);
        }
        break;
    }
  };

  return (
    <div
      data-testid="input-search-select"
      ref={rootRef}
      className={cn("flex flex-col gap-1 w-full", heights.font)}
      onBlurCapture={(e) => {
        const next = e.relatedTarget as Node | null;
        if (next && rootRef.current?.contains(next)) return;

        setTimeout(() => {
          setOpen(false);
          setHighlight(-1);
          validateOnBlur();
        }, 0);
      }}
    >
      {label && (
        <label htmlFor={id} className="text-sm text-[#3D3D3D] flex">
          {label} {required ? <Required /> : ""}
        </label>
      )}

      <div className="relative">
        <div
          className={[
            "w-full rounded-xs border bg-white px-3 py-2",
            error ? "border-red-500" : "border-[#E4E4E4]",
            disabled ? "bg-[#F5F5F5] text-[#9E9E9E]" : "",
          ].join(" ")}
          onKeyDown={onKeyDown}
          onMouseDown={() => {
            if (disabled) return;
            setOpen(true);
          }}
        >
          <div className="flex items-center gap-2">
            {children}
            <input
              ref={inputRef}
              id={id}
              name={name}
              disabled={disabled}
              value={query}
              placeholder={placeholder}
              onChange={(e) => {
                if (disabled) return;
                setQuery(e.target.value);
                setOpen(true);
                if (internalValue != null) setInternalValue(null);

                // primer highlight a un item habilitado
                requestAnimationFrame(() => {
                  const first = filteredOptions.findIndex((o) => !o.disabled);
                  setHighlight(first);
                });
              }}
              onFocus={() => !disabled && setOpen(true)}
              className="min-w-0 flex-1 bg-transparent outline-none"
              role="combobox"
              aria-expanded={open}
              aria-autocomplete="list"
            />
          </div>
        </div>

        {open && (
          <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-xs border border-[#EDEDED] bg-white shadow-md">
            {titleInPanel && (
              <div className="px-3 py-2 text-[#7A7A7A] border-b">
                {titleInPanel}
              </div>
            )}

            {filteredOptions.length === 0 ? (
              <div className="px-3 py-3 text-[#9E9E9E]">No hay resultados</div>
            ) : (
              <div
                ref={scrollParentRef}
                role="listbox"
                style={{ height: panelHeight, overflow: "auto" }}
                className="custom-scrollbar"
              >
                <div
                  style={{
                    height: virtualizer.getTotalSize(),
                    width: "100%",
                    position: "relative",
                  }}
                >
                  {virtualizer.getVirtualItems().map((vi) => {
                    const opt = filteredOptions[vi.index];
                    const selected = opt.value === internalValue;
                    const highlighted = vi.index === highlight;

                    return (
                      <div
                        key={opt.value}
                        id={`${id}-option-${vi.index}`}
                        role="option"
                        aria-selected={selected}
                        aria-disabled={opt.disabled || undefined}
                        onMouseEnter={() =>
                          !opt.disabled && setHighlight(vi.index)
                        }
                        onMouseDown={(e) => {
                          // evita perder foco antes de seleccionar
                          e.preventDefault();
                        }}
                        onClick={() => !opt.disabled && commitChange(opt.value)}
                        className={[
                          "px-3 flex items-center select-none border-b border-[#EFEFEF]",
                          opt.disabled
                            ? "opacity-50 cursor-not-allowed"
                            : "cursor-pointer",
                          selected ? "bg-[#FDE7EA]" : "",
                          !selected && highlighted ? "bg-[#F7F7F7]" : "",
                        ].join(" ")}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: rowHeight,
                          transform: `translateY(${vi.start}px)`,
                        }}
                      >
                        <span className="truncate">{opt.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InputSearchSelect;
